<script setup>
import {
    trackerTypes,
    visualizationTypes,
} from "@/constants/trackers";

import {getAvatarsForDisplay} from "@/helpers/string";
import Vue, {computed, inject} from "vue";
import trackersService from "@/services/trackers.service";
import DuplicateTracker from "@/components/trackers/DuplicateTracker.vue";
import {ref} from "vue";
import ShareActions from "@/components/trackers/shareActions/ShareActions.vue";
import SelectTrackerFolder from "@/components/manage/SelectTrackerFolder.vue";
import trackersFoldersService from "@/services/trackers-folders.service";
import {useStore} from "vue2-helpers/vuex";
import AccessSettings from "@/components/trackers/EcosystemsAccessSettings.vue";
import Avatar from "@/components/ui/Avatar.vue";

const store = useStore();

const props = defineProps(['tracker', 'folderId']);
const emit = defineEmits(['edit', 'permissionsUpdated', 'accessSettingsUpdated']);

const id = props.tracker.id;
const updateTrackers = inject('updateTrackers');
const duplicateTrackerModal = ref();
const selectFolderModal = ref(null);
const accessSettings = ref(null);

const permissionAvatars = computed(() => getAvatarsForDisplay(props.tracker.permissions));

async function setFavorite() {
    await trackersService.setFavorite(id, 1);
    updateTrackers();
}

async function unsetFavorite() {
    await trackersService.setFavorite(id, 0);
    updateTrackers();
}

async function archive() {
    await trackersService.update(id, {status: 'archive'});
    updateTrackers();
}

async function unarchive() {
    await trackersService.update(id, {status: 'draft'});
    updateTrackers();
}

async function remove() {
    if (!confirm("Delete tracker?")) {
        return;
    }

    await trackersService.delete(id);
    updateTrackers();
}

function selectFolder() {
    selectFolderModal.value.open(props.tracker.type === trackerTypes.PUBLIC ? '' : 'private', props.tracker.folder);
}

async function addToFolder(folder) {
    if (props.tracker.folder && !confirm(`This tracker already associated with folder "${props.tracker.folder.name}". Would you like to move tracker to "${folder.name}"?`)) {
        return;
    }

    await trackersFoldersService.addTrackers(folder.id, [props.tracker.id]);
    updateTrackers();

    Vue.notify({
        group: 'app',
        type: 'success',
        duration: 5000,
        text: "Tracker added to " + folder.name + " folder."
    })
}

async function removeFromFolder() {
    if (!confirm("Remove tracker from folder?")) {
        return;
    }

    await trackersService.update(id, {folder_id: null});
    updateTrackers();
}

function openAccessSettings() {
    accessSettings.value.open(props.tracker.access_type, props.tracker.ecosystems);
}

async function accessSettingsChanged(params) {
    await trackersService.saveAccessSettings(props.tracker.id, params);
    emit('accessSettingsUpdated');
}

</script>

<template>
    <div
        class="card card--bordered card--ordered card--trackers"
        :class="{'card--trackers__empty': tracker.type !== trackerTypes.ECOSYSTEMS && !tracker.description.length && !tracker.fields.length}"
    >
        <header class="card__header">
            <h5 class="heading5">
                <span v-if="tracker.is_favorite" class="icon-favorite"></span>
                <router-link :to="'/trackers/' + tracker.id">{{ tracker.name }}</router-link>
            </h5>
        </header>

        <div class="card__inner">
            <div class="card__inner__side">
                <p v-if="tracker.type === trackerTypes.ECOSYSTEMS" class="card--trackers__ecosystem">Ecosystem tracker</p>
                <div class="card--trackers__description line-clamp line-clamp4" v-html="tracker.description"></div>

                <div v-if="tracker.fields.length" class="tags-list tags-list--airy">
                    <span
                        v-for="(field, index) in tracker.fields"
                        :key="index"
                        :title="field.title ? field.title : field.value"
                        class="tags-list__tag"
                    >
                        <template v-if="field.url && field.title">
                            <a :href="field.url" target="_blank">{{field.title}}</a>
                        </template>

                        <template v-else>
                            {{field.value}}
                        </template>
                    </span>
                </div>
            </div>

            <div class="card__controls">
                <ShareActions
                    v-if="store.getters.isEnterprise"
                    :tracker-id="tracker.id"
                    :tracker-type="tracker.type"
                    :is-public="tracker.type === trackerTypes.PUBLIC"
                    :is-ecosystems="tracker.type === trackerTypes.ECOSYSTEMS"
                    :is-referral="tracker.only_referral"
                    :referral-url="tracker.referral_url"
                    @permissionsUpdated="emit('permissionsUpdated')"
                    @accessSettingsUpdated="emit('accessSettingsUpdated')"
                />

                <a v-if="tracker.user_can_edit" class="button-airy button-airy--edit" @click="emit('edit')">
                    Edit
                </a>

                <b-dropdown v-if="tracker.user_can_edit || tracker.user_can_archive" class="dropdown-airy dropdown-airy--action icons-list" boundary="viewport">
                    <template v-if="folderId === 0">
                        <b-dropdown-item
                            v-if="!tracker.is_favorite"
                            class="icons-list__favorite"
                            @click="setFavorite"
                        >
                            Mark As Favorite
                        </b-dropdown-item>

                        <b-dropdown-item
                            v-else
                            class="icons-list__favorite"
                            @click="unsetFavorite"
                        >
                            Unmark As Favorite
                        </b-dropdown-item>

                        <b-dropdown-item
                            v-if="tracker.user_can_edit && folderId === 0"
                            class="icons-list__copy"
                            @click="duplicateTrackerModal.open(tracker.id, tracker.name, tracker.real_type)"
                        >
                            Duplicate Tracker
                        </b-dropdown-item>
                    </template>

                    <b-dropdown-item
                        v-if="tracker.user_can_edit && folderId > 0"
                        class="icons-list__remove"
                        @click="removeFromFolder"
                    >
                        Remove From Folder
                    </b-dropdown-item>

                    <b-dropdown-item
                        v-if="tracker.user_can_delete"
                        class="icons-list__delete"
                        @click="remove"
                    >
                        Delete Tracker
                    </b-dropdown-item>

                    <b-dropdown-item
                        v-if="tracker.user_can_archive && tracker.status !== 'archive'"
                        class="icons-list__archive"
                        @click="archive"
                    >
                        Archive
                    </b-dropdown-item>

                    <b-dropdown-item
                        v-if="tracker.user_can_archive && tracker.status === 'archive'"
                        class="icons-list__archive"
                        @click="unarchive"
                    >
                        Unarchive
                    </b-dropdown-item>

                    <b-dropdown-item
                        v-if="tracker.user_can_edit && folderId === 0"
                        class="icons-list__add-folder"
                        @click="selectFolder"
                    >
                        Add to Folder
                    </b-dropdown-item>
                </b-dropdown>
            </div>
        </div>

        <footer class="card__footer">
            <ul class="card--trackers__info">
                <li class="card--trackers__info__private" v-if="tracker.type === trackerTypes.PRIVATE">Private</li>
                <li class="card--trackers__info__public" v-if="tracker.type === trackerTypes.PUBLIC">Public</li>

                <template v-if="tracker.companies_count > 0">
                    <li :class="{
                        'card--trackers__info__startup': tracker.type !== trackerTypes.ECOSYSTEMS,
                        'card--trackers__info__ecosystem': tracker.type === trackerTypes.ECOSYSTEMS
                    }">
                        {{ tracker.companies_count }}
                    </li>
                </template>

                <li class="card--trackers__info__pipeline" v-if="tracker.visualization_type === visualizationTypes.PIPELINE">Pipeline</li>
                <li class="card--trackers__info__timeline" v-if="tracker.visualization_type === visualizationTypes.TIMELINE">Timeline</li>

                <li class="card--trackers__info__ecosystem" v-if="tracker.access_type !== 'private'">
                    <a
                        v-if="tracker.user_permissions.includes('tracker-add-ecosystem')"
                        @click="openAccessSettings"
                    >
                        Shared
                    </a>
                    <template v-else>
                        Shared
                    </template>
                </li>

                <li v-if="tracker.folder" class="card--trackers__info__brand">
                    <a :href="`/trackers/folder/${tracker.folder.id}`" target="_blank">{{tracker.folder.name}}</a>
                </li>

                <li class="card--trackers__info__time">Modified {{ tracker.last_modified }}</li>

                <li>
                    
                <div v-if="tracker.needs_approval" class="icon-pending mt-0">
                    Pending Approval ({{tracker.approvals.approved}}/{{tracker.approvals.all}})
                </div>
                </li>
            </ul>

            <div class="card__controls">
                <b-dropdown class="dropdown--secondary ml-auto" variant="link" toggle-class="text-decoration-none" no-caret>
                    <template #button-content>
                        <ul class="card--trackers__users">
                            <li
                                v-for="(item, index) in permissionAvatars"
                                :key="index"
                                v-b-popover.hover.bottomleft="item.name"
                                :class="{'card--trackers__users__group': item.type === 'group'}"
                            >
                                <Avatar
                                    v-if="item.type === 'user'"
                                    :src="item.avatar"
                                    :username="item.name"
                                    :size="35"
                                />

                                <img v-else :src="item.avatar" class="group" alt=""/>
                            </li>

                            <li v-if="tracker.permissions.length > permissionAvatars.length" class="card--trackers__users__more">
                                <a>+{{tracker.permissions.length - permissionAvatars.length}}</a>
                            </li>
                        </ul>
                    </template>

                    <b-dropdown-header>
                        <header class="dropdown__header-inner">
                            <h4 class="heading4">Users in {{ tracker.name }}</h4>
                        </header>

                        <ul class="rows-list">
                            <li v-for="(permission, index) in tracker.permissions" :key="index">
                                <div class="rows-list__avatar">
                                    <Avatar
                                        :username="permission.name"
                                        :src="permission.avatar"
                                        :size="30"
                                    />
                                </div>

                                <h5 class="heading5 heading5--secondary">{{ permission.name }}</h5>

                                <!-- Please add here the logic first -->
                                <p class="ml-auto">{{ permission.role.name }}</p>
                                <a
                                    v-if="!store.getters.isEcosystem"
                                    v-b-popover.hover.top="permission.role.description"
                                    class="link-info"
                                >
                                </a>
                            </li>
                        </ul>
                    </b-dropdown-header>
                </b-dropdown>
            </div>
        </footer>

        <DuplicateTracker
            ref="duplicateTrackerModal"
            @trackerSaved="updateTrackers"
        />

        <SelectTrackerFolder
            ref="selectFolderModal"
            @folderSelected="addToFolder"
        />

        <AccessSettings
            v-if="store.getters.isEnterprise && tracker.type === trackerTypes.PRIVATE && tracker.user_permissions.includes('tracker-add-ecosystem')"
            ref="accessSettings"
            :from-tracker-page="true"
            @saved="accessSettingsChanged"
        />
    </div>
</template>

<style scoped>
    .popover {
        padding: 10px;
        max-width: 300px;
        width: auto;
    }
    .dropdown--secondary .dropdown-menu  {
        background: red !important;
    }
</style>
